"use client";

import { getCMSApiRoute, getRtEventApiRoute } from "@/helpers/api.util";
import { refreshAuthSession } from "@/config/axiosInstance";
import { trackGtagEvent } from "@/services/gtagAnalyticsService";
import { createTelemetryUuid } from "./playbackTelemetry";

const IMPRESSION_BATCH_LIMIT = 100;
const DEFAULT_HEARTBEAT_INTERVAL_SEC = 10;

const pendingImpressions = [];

export const createAnalyticsId = createTelemetryUuid;

export const getAnalyticsToken = () => {
  try {
    if (typeof document === "undefined") return "";

    const tokenCookie = document.cookie
      .split("; ")
      .find((cookie) => cookie.startsWith("userAccessToken="));

    return tokenCookie
      ? decodeURIComponent(tokenCookie.split("=").slice(1).join("="))
      : "";
  } catch (_error) {
    return "";
  }
};

export const normalizeContentKind = (content) => {
  const kind =
    content?.contentType?.kind ||
    content?.kind?.kind ||
    content?.kind ||
    (content?.isLive ? "live" : "");

  return kind === "sport" ? "program" : kind;
};

export const getAnalyticsMode = (content) => {
  return content?.isLive || normalizeContentKind(content) === "live"
    ? "live"
    : "vod";
};

const getAppVersion = () => process.env.NEXT_PUBLIC_APP_VERSION || "";

const buildHeaders = (token) => ({
  "Content-Type": "application/json",
  "X-Device": "web",
  "X-App-Version": getAppVersion(),
  Authorization: `Bearer ${token}`,
});

const sendPostRequest = (url, body, token, options) =>
  fetch(url, {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify(body),
    keepalive: options.keepalive,
    signal: options.signal,
  });

export const postAnalyticsJson = async (url, body, options = {}) => {
  if (typeof fetch === "undefined") return null;

  const token = options.token ?? getAnalyticsToken();
  if (!token) return null;

  try {
    const response = await sendPostRequest(url, body, token, options);
    if (response?.status !== 401 || options.skipAuthRefresh) return response;

    const refreshedToken = await refreshAuthSession();
    if (!refreshedToken) return response;

    return await sendPostRequest(url, body, refreshedToken, {
      ...options,
      skipAuthRefresh: true,
    });
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[Analytics] request failed:", error?.message || error);
    }
    return null;
  }
};

const enrichEngagementEvent = (event) => ({
  eventId: event.eventId || createAnalyticsId(),
  timestamp: event.timestamp || Date.now(),
  ...event,
});

export const sendContentView = (event) => {
  if (!event?.contentKey) return null;
  const body = enrichEngagementEvent(event);
  trackGtagEvent("content_view", body);
  return postAnalyticsJson(getCMSApiRoute("analytics/content-view"), body);
};

export const sendPlayIntent = (event) => {
  if (!event?.contentKey) return null;
  const body = enrichEngagementEvent(event);
  trackGtagEvent("play_intent", body);
  return postAnalyticsJson(getCMSApiRoute("analytics/play-intent"), body);
};

export const queueContentImpression = (event) => {
  if (!event?.contentKey) return;

  const body = enrichEngagementEvent({
    visibleMs: 1000,
    ...event,
  });

  pendingImpressions.push(body);
  trackGtagEvent("content_impressions", body);

  if (pendingImpressions.length >= IMPRESSION_BATCH_LIMIT) {
    flushContentImpressions();
  }
};

const shouldRetryImpressionBatch = (response) => {
  if (!response) return Boolean(getAnalyticsToken());
  return response.status === 429 || response.status >= 500;
};

export const flushContentImpressions = async () => {
  if (!pendingImpressions.length) return Promise.resolve(null);

  const events = pendingImpressions.splice(0, IMPRESSION_BATCH_LIMIT);
  const response = await postAnalyticsJson(
    getCMSApiRoute("analytics/content-impressions"),
    { events },
    { keepalive: true }
  );

  if (shouldRetryImpressionBatch(response)) {
    pendingImpressions.unshift(...events);
  }

  return response;
};

export const sendMediaEvent = (event, options = {}) => {
  if (!event?.playbackSessionId || !event?.eventType || !event?.contentKey)
    return null;

  const body = {
    eventId: event.eventId || createAnalyticsId(),
    timestamp: event.timestamp || Date.now(),
    ...event,
  };

  trackGtagEvent("media_event", body);
  return deliverMediaEvent(
    getRtEventApiRoute("analytics/media-event"),
    body,
    options
  );
};

// Retries reuse the already-created envelope, including ID and sequence.
const deliverMediaEvent = async (url, body, options = {}) => {
  const response = await postAnalyticsJson(url, body, options);
  if (
    body.schemaVersion === 2 &&
    !options.pageExit &&
    getAnalyticsToken() &&
    (!response || response.status >= 500)
  ) {
    return postAnalyticsJson(url, body, options);
  }
  return response;
};

export const sendSessionEnd = (event, options = {}) => {
  if (!event?.playbackSessionId || !event?.contentKey) return null;

  const body = {
    eventId: createAnalyticsId(),
    eventType: "session_end",
    timestamp: Date.now(),
    ...event,
  };
  const token = getAnalyticsToken();

  trackGtagEvent("media_event", body);

  if (options.pageExit) {
    return deliverMediaEvent(
      getRtEventApiRoute("analytics/media-event"),
      body,
      {
        token,
        pageExit: true,
        keepalive: true,
        skipAuthRefresh: true,
      }
    );
  }

  if (token) {
    return deliverMediaEvent(
      getRtEventApiRoute("analytics/media-event"),
      body,
      {
        token,
        keepalive: true,
      }
    );
  }

  return null;
};

export const buildContentAnalyticsPayload = (content, overrides = {}) => {
  const contentKey = content?.slug || content?.contentKey;
  if (!contentKey) return null;

  return {
    contentKey,
    mode: getAnalyticsMode(content),
    contentKind: normalizeContentKind(content),
    ...overrides,
  };
};

export const HEARTBEAT_INTERVAL_SEC = DEFAULT_HEARTBEAT_INTERVAL_SEC;
